import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Headphones, AlertCircle, Plus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

interface PlaceData {
  Name: string;
  City: string;
  Country: string;
  AudioFile_FR: string | null;
  AudioFile_EN: string | null;
  Image: string | null;
}

interface PlaceAudioSectionProps {
  place: PlaceData;
  language: 'FR' | 'EN';
  /** Currently playing language, when player is active. Used to highlight the right CTA. */
  activeLanguage?: 'FR' | 'EN' | null;
  onPlayPodcast: (lang: 'FR' | 'EN') => void;
  onOpenPodcastGenerator?: (locationName: string, language: 'fr' | 'en') => void;
}

export const PlaceAudioSection: React.FC<PlaceAudioSectionProps> = ({
  place,
  language,
  activeLanguage,
  onPlayPodcast,
  onOpenPodcastGenerator,
}) => {
  const { t } = useTranslation();
  const isFR = language === 'FR';
  const hasFR = !!place.AudioFile_FR;
  const hasEN = !!place.AudioFile_EN;
  const hasAudio = hasFR || hasEN;

  // ----- No audio yet: invite the user to create the podcast -----
  if (!hasAudio) {
    return (
      <section className="bg-card rounded-2xl border border-dashed border-border p-6 sm:p-8 text-center">
        <h3 className="text-lg sm:text-xl font-semibold mb-2 tracking-tight">
          {t('podcastComingSoon')}
        </h3>
        <p className="text-sm text-foreground/70 max-w-md mx-auto mb-5 leading-relaxed">
          {t('podcastComingSoonDesc')}
        </p>
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-center">
          <Button
            onClick={() => onOpenPodcastGenerator?.(place.Name, 'fr')}
            className="btn-adventure"
          >
            <Plus className="w-4 h-4 mr-2" />
            {isFR ? 'Créer en français' : 'Create in French'}
          </Button>
          <Button
            variant="outline"
            onClick={() => onOpenPodcastGenerator?.(place.Name, 'en')}
          >
            <Plus className="w-4 h-4 mr-2" />
            {isFR ? 'Créer en anglais' : 'Create in English'}
          </Button>
        </div>
      </section>
    );
  }

  // ----- Has audio: show the prominent listen CTA -----
  // Default language preference: UI language, with fallback to whichever exists
  const primaryLang: 'FR' | 'EN' =
    isFR ? (hasFR ? 'FR' : 'EN') : hasEN ? 'EN' : 'FR';
  const secondaryLang: 'FR' | 'EN' = primaryLang === 'FR' ? 'EN' : 'FR';
  const hasSecondary = secondaryLang === 'FR' ? hasFR : hasEN;

  return (
    <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-background shadow-lg">
      <div className="relative p-5 sm:p-6">
        {/* Decorative blob */}
        <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary/15 blur-3xl pointer-events-none" />

        <div className="relative flex items-start gap-4">
          {/* Cover image */}
          <div className="relative flex-shrink-0">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden shadow-md bg-muted">
              {place.Image ? (
                <img
                  src={place.Image}
                  alt={place.Name}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full bg-gradient-earth flex items-center justify-center">
                  <Headphones className="w-8 h-8 text-white/80" />
                </div>
              )}
            </div>
            <span className="absolute -top-1.5 -left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold shadow-md">
              <Headphones className="w-3 h-3" />
              {isFR ? 'PODCAST' : 'PODCAST'}
            </span>
          </div>

          {/* Title + descriptor */}
          <div className="flex-1 min-w-0 pt-1">
            <p className="text-[11px] uppercase tracking-wide font-semibold text-primary mb-0.5">
              {isFR ? '3 minutes pour découvrir' : '3-minute discovery'}
            </p>
            <h2 className="text-base sm:text-lg font-bold leading-tight line-clamp-2">
              {isFR
                ? `Plongez dans l'histoire de ${place.Name}`
                : `Dive into the story of ${place.Name}`}
            </h2>
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {t('podcastAudioDesc')}
            </p>
          </div>
        </div>

        {/* Play CTA buttons */}
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => onPlayPodcast(primaryLang)}
            className={cn(
              'flex-1 h-12 rounded-full btn-adventure shadow-md hover:shadow-lg transition-shadow text-sm font-semibold',
              activeLanguage === primaryLang && 'ring-2 ring-primary/50 ring-offset-2 ring-offset-background'
            )}
          >
            <Play className="w-5 h-5 mr-1.5 fill-current" />
            {isFR
              ? `Écouter${primaryLang === 'EN' ? ' en anglais' : ''}`
              : `Listen${primaryLang === 'FR' ? ' in French' : ''}`}
          </Button>

          {hasSecondary ? (
            <Button
              variant="outline"
              onClick={() => onPlayPodcast(secondaryLang)}
              className={cn(
                'sm:w-auto h-12 rounded-full text-sm font-semibold border-primary/30 hover:bg-primary/5',
                activeLanguage === secondaryLang && 'ring-2 ring-primary/50 ring-offset-2 ring-offset-background'
              )}
              aria-label={
                secondaryLang === 'FR'
                  ? isFR
                    ? 'Écouter en français'
                    : 'Listen in French'
                  : isFR
                    ? 'Écouter en anglais'
                    : 'Listen in English'
              }
            >
              <Play className="w-4 h-4 mr-1.5" />
              {secondaryLang}
            </Button>
          ) : (
            onOpenPodcastGenerator && (
              <Button
                variant="outline"
                onClick={() =>
                  onOpenPodcastGenerator(
                    place.Name,
                    secondaryLang === 'FR' ? 'fr' : 'en'
                  )
                }
                className="sm:w-auto h-12 rounded-full text-sm border-dashed border-muted-foreground/40 text-muted-foreground hover:text-foreground"
                aria-label={
                  isFR
                    ? `Créer la version ${secondaryLang === 'FR' ? 'française' : 'anglaise'}`
                    : `Create ${secondaryLang === 'FR' ? 'French' : 'English'} version`
                }
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {secondaryLang}
              </Button>
            )
          )}
        </div>

        {/* Helper line */}
        <p className="text-[11px] text-muted-foreground mt-3 flex items-center justify-center gap-1.5">
          <AlertCircle className="w-3 h-3" />
          {isFR
            ? 'Le lecteur reste accessible en bas de page'
            : 'The player stays accessible at the bottom of the page'}
        </p>
      </div>
    </Card>
  );
};
